import { NavLink, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import {
  LayoutDashboard, Users, Megaphone, Contact, FileText,
  Shield, BarChart3, Radio, Fingerprint, Send, Download, Filter, MessageCircle,
  LogOut, User, ShieldCheck, Clock, Zap, Ban, Activity, CheckCircle, Copy, Tag, Smartphone,
  Menu, X, UsersRound, CircleDot, Monitor, Code2
} from "lucide-react";

const navItems = [
  { path: "/", label: "Panel de Control", icon: LayoutDashboard },
  { path: "/profiles", label: "Perfiles", icon: Users },
  { path: "/campaigns", label: "Campanas", icon: Megaphone },
  { path: "/contacts", label: "Contactos", icon: Contact },
  { path: "/templates", label: "Plantillas", icon: FileText },
  { path: "/manual-send", label: "Envio Manual", icon: Send },
  { path: "/rcs-filter", label: "Filtrado RCS", icon: Filter },
  { path: "/replies", label: "Respuestas", icon: MessageCircle },
  { path: "/wa-groups", label: "Grupos WA", icon: UsersRound },
  { path: "/wa-status", label: "Estados WA", icon: CircleDot },
  { path: "/blacklist", label: "Blacklist", icon: Ban },
  { path: "/health", label: "Monitor Salud", icon: Activity },
  { path: "/changelog", label: "Versiones", icon: Tag },
  { path: "/settings", label: "Seguridad", icon: Shield },
  { path: "/antidetect", label: "Anti-Deteccion", icon: Fingerprint },
  { path: "/reports", label: "Reportes", icon: BarChart3 },
  { path: "/developers", label: "API", icon: Code2 },
];

const adminNavItems = [
  { path: "/admin", label: "Administracion", icon: ShieldCheck },
];

export const Sidebar = () => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloaded, setDownloaded] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const DOWNLOAD_URL = window.location.origin + "/api/extension/download";
  const DOWNLOAD_PAGE = window.location.origin + "/download-extension.html";

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const response = await fetch(DOWNLOAD_URL);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.setAttribute('download', 'rcs-bulk-sender-extension.zip');
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setDownloaded(true);
    } catch (err) {
      // error silenced
    }
    setDownloading(false);
  };

  const copyUrl = () => {
    navigator.clipboard.writeText(DOWNLOAD_URL).then(() => {
      alert('URL copiada.');
    });
  };

  const sidebarContent = (
    <>
      {/* Header */}
      <div className="p-4 lg:p-6 border-b border-white/5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center flex-shrink-0">
              <Radio className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-lg font-bold tracking-tight text-white uppercase">
                RCS Sender
              </h1>
              <p className="text-[10px] font-mono text-zinc-500 tracking-widest uppercase">
                Panel de Control
              </p>
            </div>
          </div>
          {/* Close button on mobile */}
          <button
            onClick={() => setMobileOpen(false)}
            className="lg:hidden text-zinc-400 hover:text-white p-1"
            data-testid="close-sidebar-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-3 px-2 lg:px-3 space-y-0.5 overflow-y-auto">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path ||
            (item.path !== "/" && location.pathname.startsWith(item.path));
          return (
            <NavLink
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.path.replace("/", "") || "dashboard"}`}
              className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                isActive
                  ? "bg-blue-600/10 text-blue-400 border-l-2 border-blue-500"
                  : "text-zinc-400 hover:text-zinc-100 hover:bg-white/[0.03]"
              }`}
            >
              <item.icon className="w-4 h-4 flex-shrink-0" />
              <span>{item.label}</span>
            </NavLink>
          );
        })}

        {user && (user.role === 'admin' || user.role === 'superadmin') && (
          <div className="pt-3 mt-3 border-t border-zinc-800">
            <p className="px-3 text-[10px] font-semibold text-zinc-600 uppercase tracking-wider mb-2">
              Admin
            </p>
            {adminNavItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  data-testid="nav-admin"
                  className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                    isActive
                      ? "bg-purple-600/10 text-purple-400 border-l-2 border-purple-500"
                      : "text-zinc-400 hover:text-zinc-100 hover:bg-white/[0.03]"
                  }`}
                >
                  <item.icon className="w-4 h-4 flex-shrink-0" />
                  <span>{item.label}</span>
                </NavLink>
              );
            })}
          </div>
        )}
      </nav>

      {/* Footer */}
      <div className="p-3 border-t border-white/5 space-y-2">
        <button
          onClick={() => setShowDownloadModal(true)}
          data-testid="download-extension-btn"
          className="flex items-center gap-2 bg-blue-600/10 hover:bg-blue-600/20 text-blue-400 border border-blue-500/20 rounded-lg p-2.5 text-xs font-medium transition-colors duration-200 w-full text-left"
        >
          <Download className="w-4 h-4 flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-blue-300 font-semibold text-xs">Extension Chrome</p>
            <p className="text-[9px] text-blue-400/60 font-mono">RCS - Modo Desarrollador</p>
          </div>
        </button>

        <NavLink
          to="/desktop-app"
          data-testid="download-desktop-app-btn"
          className="flex items-center gap-2 bg-cyan-600/10 hover:bg-cyan-600/20 text-cyan-400 border border-cyan-500/20 rounded-lg p-2.5 text-xs font-medium transition-colors duration-200 w-full text-left"
        >
          <Monitor className="w-4 h-4 flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-cyan-300 font-semibold text-xs">App Escritorio</p>
            <p className="text-[9px] text-cyan-400/60 font-mono">RCS Multi-cuenta - Windows</p>
          </div>
        </NavLink>

        <a
          href={`${window.location.origin}/api/android-app/download`}
          download
          data-testid="download-android-btn"
          className="flex items-center gap-2 bg-green-600/10 hover:bg-green-600/20 text-green-400 border border-green-500/20 rounded-lg p-2.5 text-xs font-medium transition-colors duration-200 w-full text-left"
        >
          <Smartphone className="w-4 h-4 flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-green-300 font-semibold text-xs">APK Android</p>
            <p className="text-[9px] text-green-400/60 font-mono">WhatsApp - Android Studio</p>
          </div>
        </a>

        <a
          href={`${window.location.origin}/api/whatsapp-extension/download`}
          download
          data-testid="download-wa-extension-btn"
          className="flex items-center gap-2 bg-emerald-600/10 hover:bg-emerald-600/20 text-emerald-400 border border-emerald-500/20 rounded-lg p-2.5 text-xs font-medium transition-colors duration-200 w-full text-left"
        >
          <Monitor className="w-4 h-4 flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-emerald-300 font-semibold text-xs">Extension WA Web</p>
            <p className="text-[9px] text-emerald-400/60 font-mono">WhatsApp Web - Chrome</p>
          </div>
        </a>

        {/* User Info */}
        {user && (
          <div className="bg-zinc-900/50 rounded-lg p-2.5">
            {user.plan === 'trial' && user.trial_days_remaining !== undefined && (
              <div className="mb-2 flex items-center gap-2 bg-amber-500/10 border border-amber-500/20 rounded-lg px-2 py-1.5">
                <Clock className="w-3 h-3 text-amber-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-medium text-amber-400">Prueba Gratuita</p>
                  <p className="text-[10px] text-amber-400/70">
                    {user.trial_days_remaining > 0
                      ? `${user.trial_days_remaining} dias restantes`
                      : 'Expira hoy'}
                  </p>
                </div>
              </div>
            )}
            {user.plan && user.plan !== 'trial' && (
              <div className="mb-2 flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 rounded-lg px-2 py-1.5">
                <Zap className="w-3 h-3 text-blue-400 flex-shrink-0" />
                <p className="text-[10px] font-medium text-blue-400">
                  Plan {user.plan_info?.name || user.plan.charAt(0).toUpperCase() + user.plan.slice(1)}
                </p>
              </div>
            )}

            <div className="flex items-center gap-2">
              {user.picture ? (
                <img src={user.picture} alt={user.name} className="w-7 h-7 rounded-full flex-shrink-0" />
              ) : (
                <div className="w-7 h-7 rounded-full bg-zinc-700 flex items-center justify-center flex-shrink-0">
                  <User className="w-3.5 h-3.5 text-zinc-400" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-medium text-white truncate">{user.name}</p>
                <p className="text-[9px] text-zinc-500 truncate">{user.email}</p>
              </div>
            </div>
            <button
              onClick={logout}
              className="mt-2 flex items-center justify-center gap-2 w-full px-3 py-1.5 rounded-md bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-white text-xs transition-colors"
            >
              <LogOut className="w-3 h-3" />
              Cerrar Sesion
            </button>
            <p className="text-center text-[9px] text-zinc-600 mt-2 font-mono">Plataforma v1.10.0</p>
          </div>
        )}
      </div>

      {/* Download Modal */}
      {showDownloadModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[200] p-4" onClick={() => setShowDownloadModal(false)}>
          <div className="bg-zinc-900 border border-white/10 rounded-2xl p-5 w-full max-w-sm shadow-2xl" onClick={e => e.stopPropagation()}>
            <h3 className="text-white font-bold text-base mb-1">Descargar Extension Chrome</h3>
            <p className="text-zinc-400 text-xs mb-4">Elige como descargar el archivo ZIP:</p>

            <button
              onClick={handleDownload}
              disabled={downloading}
              data-testid="auto-download-btn"
              className="w-full flex items-center gap-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl p-3 text-sm font-semibold mb-3 transition disabled:opacity-50"
            >
              {downloaded ? <CheckCircle className="w-4 h-4" /> : <Download className="w-4 h-4" />}
              {downloading ? 'Preparando...' : downloaded ? 'Descargado! (revisa Descargas)' : 'Descarga Automatica'}
            </button>

            <p className="text-zinc-500 text-xs mb-2">O copia esta URL:</p>
            <div className="flex gap-2 mb-4">
              <div className="flex-1 bg-zinc-800 rounded-lg px-3 py-2 text-[10px] text-zinc-300 font-mono overflow-hidden text-ellipsis whitespace-nowrap">
                .../api/extension/download
              </div>
              <button onClick={copyUrl} className="bg-zinc-700 hover:bg-zinc-600 text-white rounded-lg px-3 py-2 text-xs transition">
                <Copy className="w-3 h-3" />
              </button>
            </div>

            <a
              href={DOWNLOAD_PAGE}
              target="_blank"
              rel="noreferrer"
              className="w-full flex items-center justify-center gap-2 bg-zinc-700 hover:bg-zinc-600 text-white rounded-xl p-3 text-sm font-medium mb-3 transition"
              data-testid="open-download-tab-btn"
            >
              Abrir pagina de descarga
            </a>

            <button onClick={() => setShowDownloadModal(false)} className="w-full text-zinc-500 text-xs hover:text-zinc-300 transition">
              Cerrar
            </button>
          </div>
        </div>
      )}
    </>
  );

  return (
    <>
      {/* Mobile hamburger button */}
      <button
        onClick={() => setMobileOpen(true)}
        data-testid="mobile-menu-btn"
        className="lg:hidden fixed top-3 left-3 z-[60] bg-zinc-900 border border-zinc-800 rounded-lg p-2 text-zinc-300 hover:text-white transition-colors"
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/60 z-[70]"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar - desktop: fixed, mobile: slide-in */}
      <aside
        data-testid="sidebar-nav"
        className={`fixed left-0 top-0 h-screen w-64 bg-zinc-950/95 backdrop-blur-xl border-r border-white/5 flex flex-col z-[80] transition-transform duration-300 ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        {sidebarContent}
      </aside>
    </>
  );
};

export default Sidebar;
